import React from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

/**
 * Analytics Error Boundary Component
 * 
 * Catches JavaScript errors in analytics components and displays a fallback UI
 * Provides options to retry or navigate away from the error state
 */
class AnalyticsErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null 
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error for monitoring
    console.error('Analytics Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Log to external error tracking service
    if (window.Sentry) {
      window.Sentry.captureException(error, {
        tags: {
          component: 'AnalyticsErrorBoundary',
          section: 'analytics'
        },
        extra: errorInfo
      });
    }

    // Track error event for analytics
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: error.toString(),
        fatal: false
      });
    }
  }

  handleRetry = () => {
    this.setState({ 
      hasError: false, 
      error: null, 
      errorInfo: null 
    });
  };

  render() {
    if (this.state.hasError) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '400px',
            p: 4,
            textAlign: 'center'
          }}
        >
          <Paper
            elevation={3}
            sx={{
              p: 4,
              maxWidth: 600,
              width: '100%'
            }}
          >
            {/* Error Icon */}
            <ErrorIcon 
              color="error" 
              sx={{ 
                fontSize: 64, 
                mb: 2 
              }} 
            />

            {/* Error Title */}
            <Typography 
              variant="h5" 
              component="h1" 
              gutterBottom
              color="error"
            >
              Analytics Dashboard Error
            </Typography>

            {/* Error Description */}
            <Typography 
              variant="body1" 
              color="text.secondary" 
              paragraph
              sx={{ mb: 3 }}
            >
              We encountered an issue loading the analytics dashboard. 
              This might be due to a temporary connectivity issue or a problem with the data.
            </Typography>

            {/* Error Details (Development only) */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 3, 
                  textAlign: 'left',
                  '& .MuiAlert-message': {
                    width: '100%'
                  }
                }}
              >
                <AlertTitle>Error Details (Development Mode)</AlertTitle>
                <Typography 
                  variant="body2" 
                  component="pre" 
                  sx={{ 
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    overflow: 'auto',
                    maxHeight: 200,
                    whiteSpace: 'pre-wrap'
                  }}
                >
                  {this.state.error.toString()}
                  {this.state.errorInfo.componentStack}
                </Typography>
              </Alert>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                startIcon={<RefreshIcon />}
                onClick={this.handleRetry}
                color="primary"
              >
                Try Again
              </Button>

              <Button
                variant="outlined"
                startIcon={<HomeIcon />}
                onClick={() => {
                  // Navigate to dashboard
                  if (this.props.navigate) {
                    this.props.navigate('/dashboard');
                  } else {
                    window.location.href = '/dashboard';
                  }
                }}
              >
                Back to Dashboard
              </Button>
            </Box>

            {/* Support Information */}
            <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
              <Typography variant="body2" color="text.secondary">
                If this problem persists, please try refreshing the page or contact support.
              </Typography>
            </Box>
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

/**
 * HOC wrapper to provide navigation prop to class component
 */
const AnalyticsErrorBoundaryWithNavigation = ({ children }) => {
  const navigate = useNavigate();
  
  return (
    <AnalyticsErrorBoundary navigate={navigate}>
      {children}
    </AnalyticsErrorBoundary>
  );
};

export default AnalyticsErrorBoundaryWithNavigation;